import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import SectionRenderer from "../components/SectionRenderer";
import SEO from "../components/SEO";
import ContactForm from "../components/ContactForm";
import Breadcrumbs from "../components/Breadcrumbs";
import { getPage, getLocations, getPhysician, getSettings } from "../lib/api";
import { Helmet } from "react-helmet-async";
import { useLang } from "../lib/i18n";

export default function PublicPage({ slug: forcedSlug }) {
  const params = useParams();
  const loc = useLocation();
  const { lang, t } = useLang();
  const slug = forcedSlug || params.slug || "home";
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [physician, setPhysician] = useState(null);
  const [settings, setSettings] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    setData(null); setError(null);
    Promise.all([getPage(slug, lang), getLocations(lang), getPhysician(lang), getSettings(lang)])
      .then(([d, l, p, s]) => {
        if (cancel) return;
        setData(d); setLocations(l); setPhysician(p); setSettings(s);
      })
      .catch((e) => !cancel && setError(e?.response?.status === 404 ? "404" : "error"));
    return () => { cancel = true; };
  }, [slug, lang]);

  if (error === "404") return <div className="container-x py-32 text-center"><h1 className="text-3xl font-bold text-navy-dark">{t("pageNotFound")}</h1></div>;
  if (!data) return <div className="container-x py-32 text-center text-ink-muted">{t("loading")}</div>;

  const { page, sections } = data;

  // Inject FAQPage JSON-LD if FAQ accordion present
  const faqSec = sections.find((s) => s.type === "faq_accordion");
  const faqJsonLd = faqSec && faqSec.faq_items?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqSec.faq_items.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      }
    : null;

  return (
    <>
      <SEO page={page} locations={locations} physician={physician} settings={settings} lang={lang} />
      {faqJsonLd && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        </Helmet>
      )}
      <Breadcrumbs pathname={loc.pathname} />
      {/* Subpage H1 banner — Preventum image background unless this page already has a hero */}
      {page.show_page_hero !== false && !sections.some((s) => s.type === "fullscreen_video_slider_hero" && s.h_tag === "h1") && page.h1_de && (
        <header
          className="relative overflow-hidden text-white isolate bg-navy-dark"
          data-testid="page-h1-hero"
        >
          {page.og_image_url && (
            <img
              src={page.og_image_url}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-50"
              aria-hidden
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-navy/90 via-navy-dark/85 to-navy-support/80" />
          <div className="absolute inset-0 grain opacity-40" />
          <div className="container-x relative py-14 md:py-24">
            {page.nav_label_de && (
              <div className="eyebrow text-white/75 mb-3 md:mb-4">{page.nav_label_de}</div>
            )}
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white max-w-4xl leading-tight tracking-tight">{page.h1_de}</h1>
            {page.meta_description_de && (
              <p className="text-base md:text-xl text-white/90 mt-4 max-w-3xl leading-relaxed">{page.meta_description_de}</p>
            )}
          </div>
        </header>
      )}
      {sections.map((s, index) => (
        <SectionRenderer
          key={s.id}
          section={page.show_page_hero === false && index === 0 ? { ...s, h_tag: "h1" } : s}
          locations={locations}
          contactForm={s.type === "contact_form" ? <ContactForm /> : null}
        />
      ))}
    </>
  );
}
